/**
 * Frontend security utilities and configurations
 */

// Content Security Policy configuration for React
export const CSP_CONFIG = {
  'default-src': ["'self'", "https://fonts.googleapis.com", "https://fonts.gstatic.com"],
  'script-src': [
    "'self'",
    "'unsafe-inline'", // Required for React development
    "'unsafe-eval'", // Required for React development
    "https://js.stripe.com",
    "https://checkout.stripe.com",
    "https://player.vimeo.com",
    "https://embed.cloudinary.com",
    "https://vercel.live"
  ],
  'style-src': [
    "'self'",
    "'unsafe-inline'", // Required for styled-components
    "https://fonts.googleapis.com",
    "https://cdn.jsdelivr.net"
  ],
  'style-src-elem': [
    "'self'",
    "'unsafe-inline'",
    "https://fonts.googleapis.com",
    "https://cdn.jsdelivr.net"
  ],
  'img-src': [
    "'self'",
    "data:",
    "https:",
    "blob:",
    "https://res.cloudinary.com",
    "https://player.vimeo.com",
    "https://i.vimeocdn.com",
    "https://vumbnail.com"
  ],
  'font-src': [
    "'self'",
    "data:",
    "https://fonts.gstatic.com",
    "https://fonts.googleapis.com",
    "https://cdn.jsdelivr.net"
  ],
  'connect-src': [
    "'self'",
    "https://api.stripe.com",
    "https://api.vimeo.com",
    "https://api.cloudinary.com",
    "https://res.cloudinary.com",
    "https://elderfit.onrender.com",
    "wss://elderfit.onrender.com",
    "https://vercel.live",
    "https://vercel.com",
    "https://platform.dash.cloudflare.com",
    "https://customer-apz9t94a2fzrv1a9.cloudflarestream.com",
    "https://localhost:3000",
    "https://localhost:3001",
    "https://localhost:31415",
    "https://localhost:31416"
  ],
  'frame-src': [
    "'self'",
    "https://js.stripe.com",
    "https://checkout.stripe.com",
    "https://player.vimeo.com",
    "https://embed.cloudinary.com",
    "https://customer-apz9t94a2fzrv1a9.cloudflarestream.com",
    "https://vercel.live"
  ],
  'object-src': ["'none'"],
  'media-src': [
    "'self'",
    "https://player.vimeo.com",
    "https://res.cloudinary.com",
    "https://stream.cloudflare.com"
  ],
  'worker-src': ["'self'", "blob:"],
  'child-src': ["'self'", "blob:"],
  'form-action': ["'self'"],
  'base-uri': ["'self'"],
  'manifest-src': ["'self'"]
};

// Generate CSP header string
export const generateCSP = () => {
  return Object.entries(CSP_CONFIG)
    .map(([directive, sources]) => `${directive} ${sources.join(' ')}`)
    .join('; ');
};

// Security headers for meta tags
export const SECURITY_META_TAGS = {
  'Content-Security-Policy': generateCSP(),
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=(), payment=(self), usb=()'
};

// Input sanitization utilities
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove potentially dangerous characters
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/script/gi, '') // Remove script tags
    .replace(/iframe/gi, '') // Remove iframe tags
    .replace(/object/gi, '') // Remove object tags
    .replace(/embed/gi, '') // Remove embed tags
    .replace(/form/gi, '') // Remove form tags
    .trim();
};

// Validate email format
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate password strength
export const validatePassword = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  return {
    isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
    errors: {
      minLength: password.length < minLength,
      noUpperCase: !hasUpperCase,
      noLowerCase: !hasLowerCase,
      noNumbers: !hasNumbers,
      noSpecialChar: !hasSpecialChar
    }
  };
};

// Sanitize HTML content
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return html;
  
  // Create a temporary div to parse HTML
  const temp = document.createElement('div');
  temp.textContent = html;
  return temp.innerHTML;
};

// Validate URL
export const validateURL = (url) => {
  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

// Generate secure random string
export const generateSecureToken = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Check if running in secure context
export const isSecureContext = () => {
  return window.isSecureContext || window.location.protocol === 'https:';
};

// Security event logging
export const logSecurityEvent = (event, details = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('Security Event:', {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }
  
  // In production, you might want to send this to a security monitoring service
  // Example: sendToSecurityService(event, details);
};

// Detect potential XSS attempts
export const detectXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i,
    /onload=/i,
    /onerror=/i,
    /onclick=/i,
    /eval\(/i,
    /expression\(/i,
    /url\(/i,
    /import\s+/i,
    /require\s*\(/i
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

// Rate limiting for client-side operations
export class ClientRateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = [];
  }
  
  canMakeRequest() {
    const now = Date.now();
    
    // Remove old requests outside the window
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    
    if (this.requests.length >= this.maxRequests) {
      return false;
    }
    
    this.requests.push(now);
    return true;
  }
  
  getTimeUntilReset() {
    if (this.requests.length === 0) return 0;
    const oldestRequest = Math.min(...this.requests);
    return Math.max(0, this.windowMs - (Date.now() - oldestRequest));
  }
}

const securityUtils = {
  CSP_CONFIG,
  generateCSP,
  SECURITY_META_TAGS,
  sanitizeInput,
  validateEmail,
  validatePassword,
  sanitizeHTML,
  validateURL,
  generateSecureToken,
  isSecureContext,
  logSecurityEvent,
  detectXSS,
  ClientRateLimiter
};

export default securityUtils;
